import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import type { Entity } from '../../../../model/entity.model';
import type { ChatEntry, EntityChatPage } from '../chat/entity-chat-session';
// Was (2026-10-06, nav 8b Task 4 fix round 1 — no live template here binds `<app-studio-history>` any
// more; this import only fed the now-removed `imports` array entry):
// import { StudioHistoryComponent } from '../../../ui-editor/spotlight/shell/studio-history.component';
import type { HistoryEntry } from '../../../ui-editor/spotlight/shell/history.util';
// Nav 8b Task 4 — the conversation tab now shows the tab's one shared history, not this chat's own.
import { SharedHistoryComponent } from '../../../../studio-nav/shared-history.component';
import { buildDetail, buildOverview, MISSING_ENTITY, OPEN_NEW_KEY, type EntityDetail, type FieldNode, type Overview, type PanelState, type Tone } from './inspector-model';

export type PanelTab = 'entities' | 'conversation';
type CrudKey = 'create' | 'read' | 'update' | 'delete';

/**
 * THE ENTITY CHAT'S RIGHT PANEL (plan 5; design docs/superpowers/specs/2026-10-04-entity-panel-design.md; Q50–Q51)
 * — the entity page's inspector and the shared conversation history. It renders `inspector-model` and keeps only view
 * state (tab, the inspected entity, open fields, search); every change is an event the chat routes to the page.
 * Its tone is `light`, which the chat binds to the Entities page's sun/moon switch, as it does for its bar.
 */
@Component({
  selector: 'app-entity-panel',
  standalone: true,
  // Was (2026-10-06, nav 8b Task 4 — the conversation tab now shows the tab's one shared history):
  // imports: [CommonModule, StudioHistoryComponent],
  // Was (2026-10-06, nav 8b Task 4 fix round 1 — `StudioHistoryComponent` dropped: no live template
  // binds `<app-studio-history>` any more, only a Was-commented one; a dead NG8113 import):
  // imports: [CommonModule, StudioHistoryComponent, SharedHistoryComponent],
  imports: [CommonModule, SharedHistoryComponent],
  templateUrl: './entity-panel.component.html',
})
export class EntityPanelComponent implements OnChanges {
  @Input({ required: true }) page!: EntityChatPage;
  @Input() messages: readonly ChatEntry[] = [];
  @Input() unsaved = 0;
  @Input() tab: PanelTab = 'entities';
  /** The field the chat reads as "this" — the panel shows it open and highlighted. */
  @Input() focus: { key: string; path: string[]; name: string } | null = null;
  /**
   * The white panel, for an Entities page switched to white, so the page never carries a dark slab. False is the dark
   * panel every page had, class for class.
   */
  @Input() light = false;
  @Output() tabChange = new EventEmitter<PanelTab>();
  @Output() closed = new EventEmitter<void>();
  @Output() fieldPicked = new EventEmitter<{ key: string; entity: string; path: string[]; name: string }>();
  @Output() openEntity = new EventEmitter<Entity>();
  @Output() editField = new EventEmitter<{ path: string[]; name: string }>();
  /** An example sentence the builder clicked — the chat puts it in the bar. */
  @Output() prompt = new EventEmitter<string>();

  inspectedKey: string | null = null;
  search = '';
  /** Plan 6 T1 — a link to a deleted entity is not a button. */
  readonly missing = MISSING_ENTITY;
  private readonly expanded = new Set<string>();
  // Was (2026-10-04, plan 5 T3 polish, B3 — design copy, review I1, ruled):
  // readonly examplePrompts = ['create a Customer with a name, phone and email', 'make a local Status entity with rows Open and Closed'];
  readonly examplePrompts = ['create a Customer with a name and phone', 'make a local Status entity with Open and Closed'];
  readonly crudKeys: readonly { key: CrudKey; letter: string; word: string }[] = [
    { key: 'create', letter: 'C', word: 'Create' }, { key: 'read', letter: 'R', word: 'Read' },
    { key: 'update', letter: 'U', word: 'Update' }, { key: 'delete', letter: 'D', word: 'Delete' },
  ];

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['focus'] && this.focus) {
      this.inspectedKey = this.focus.key;
      this.expanded.add(this.fieldKey(this.focus.path, this.focus.name));
    }
  }

  get state(): PanelState {
    return { entities: this.page.entities(), open: this.page.open(), unsaved: this.unsaved, roles: this.page.roles() };
  }
  get overview(): Overview { return buildOverview(this.state, this.search); }
  // Was (2026-10-04, plan 5 T3 polish, B5 — review M1): get detail(): EntityDetail | null { return this.inspectedKey ? buildDetail(this.inspectedKey, this.state) : null; }
  /** A new entity inspected before its first save keeps being inspected after it: its key moves to its id (T3 polish B5). */
  get detail(): EntityDetail | null {
    if (!this.inspectedKey) return null;
    const open = this.page.open();
    const key = this.inspectedKey === OPEN_NEW_KEY && open?.id ? open.id : this.inspectedKey;
    return buildDetail(key, this.state);
  }
  // Was (2026-10-04, plan 6 — no `notice`, so the chat's own lines read as the model's answers):
  // get historyEntries(): HistoryEntry[] { return this.messages.map((m) => ({ role: m.role, text: m.text, group: m.requestId })); }
  // Nav 8b Task 4 — the conversation tab now shows app-shared-history; this getter is no longer bound
  // there, kept in case other code reads it (`messages` input is kept the same way).
  get historyEntries(): HistoryEntry[] { return this.messages.map((m) => ({ role: m.role, text: m.text, group: m.requestId, notice: m.notice })); }
  // Was (2026-10-04, plan 5 final review, I3 — never said WHY Open on page was waiting):
  // /** The page's own rule: another entity opens only when the open one has no unsaved changes. */
  // get openBlocked(): boolean { return !!this.page.open() && this.unsaved > 0; }
  /** Why Open on page must wait, in the page's own terms — null when it may open (the chat refuses the same states). */
  get openBlockedReason(): string | null {
    if (this.page.fieldFormOpen()) return 'Close the field form first.';
    if (this.page.permissionsOpen()) return 'Close Role permissions first.';
    if (this.page.sampleRowsOpen()) return 'Close Predefined rows first.';
    if (this.page.open() && this.unsaved > 0) return `Save or close ${this.openName} first.`;
    return null;
  }
  get openBlocked(): boolean { return this.openBlockedReason !== null; }
  get openName(): string { return this.page.open()?.name || 'the open entity'; }

  inspect(key: string): void { this.inspectedKey = key; this.expanded.clear(); }
  back(): void { this.inspectedKey = null; this.expanded.clear(); }
  open(key: string): void {
    if (this.openBlocked) return;
    // Plan 5 final review, C1 — a hidden entity must never be opened on the page.
    if (this.detail?.hidden) return;
    const e = this.page.entities().find((x) => x.id === key);
    if (e) this.openEntity.emit(e);
  }
  toggle(d: EntityDetail, f: FieldNode): void {
    const k = this.fieldKey(f.path, f.name);
    if (this.expanded.has(k)) { this.expanded.delete(k); return; }
    this.expanded.add(k);
    // Was (2026-10-04, plan 5 final review, I6 — "this" named a field of an entity that was not open):
    // this.fieldPicked.emit({ key: d.key, entity: d.name, path: f.path, name: f.name });
    if (d.isOpen) this.fieldPicked.emit({ key: d.key, entity: this.page.open()?.name ?? d.name, path: f.path, name: f.name });
  }
  isExpanded(f: FieldNode): boolean { return this.expanded.has(this.fieldKey(f.path, f.name)); }
  // Was (2026-10-04, plan 5 final review, I6 — a focus taken before the entity's first save stopped matching after it):
  // isFocused(d: EntityDetail, f: FieldNode): boolean {
  //   return !!this.focus && this.focus.key === d.key && this.fieldKey(this.focus.path, this.focus.name) === this.fieldKey(f.path, f.name);
  // }
  isFocused(d: EntityDetail, f: FieldNode): boolean {
    if (!this.focus) return false;
    const key = this.focus.key === OPEN_NEW_KEY && d.isOpen ? d.key : this.focus.key;
    return key === d.key && this.fieldKey(this.focus.path, this.focus.name) === this.fieldKey(f.path, f.name);
  }
  /** Rows are kept across checks by identity, not object (the model is rebuilt each check) — keeps focus and clicks (final review I1). */
  byKey(_: number, x: { key: string }): string { return x.key; }
  // Was (2026-10-04, plan 5 final fix, live check — Angular calls a trackBy WITHOUT `this`, so this threw on every field list):
  // byField(_: number, f: FieldNode): string { return this.fieldKey(f.path, f.name); }
  /** ⚠ A trackBy never uses `this` — Angular calls it unbound. */
  byField(_: number, f: FieldNode): string { return [...f.path, f.name].join('›'); }
  byRole(_: number, a: { role: string }): string { return a.role; }
  byIndex(index: number): number { return index; }
  // Was (2026-10-15), one tone — the dark map below, unchanged, was the whole body:
  //   return { amber: 'bg-amber-500/15 text-amber-300', sky: 'bg-sky-500/15 text-sky-300', emerald: 'bg-emerald-500/15 text-emerald-300', rose: 'bg-rose-500/15 text-rose-300' }[tone];
  /** A status chip in the panel's tone. The pale 300 text of the dark chip would vanish on white, so white takes 800 on 100. */
  chipClass(tone: Tone): string {
    return this.light
      ? { amber: 'bg-amber-100 text-amber-800', sky: 'bg-sky-100 text-sky-800', emerald: 'bg-emerald-100 text-emerald-800', rose: 'bg-rose-100 text-rose-800' }[tone]
      : { amber: 'bg-amber-500/15 text-amber-300', sky: 'bg-sky-500/15 text-sky-300', emerald: 'bg-emerald-500/15 text-emerald-300', rose: 'bg-rose-500/15 text-rose-300' }[tone];
  }
  // Was (2026-10-15), one tone — the dark map below, unchanged, was the whole body:
  //   return {
  //     amber: 'bg-amber-500/10 text-amber-200 border border-amber-500/20', sky: 'bg-sky-500/10 text-sky-200 border border-sky-500/20',
  //     emerald: 'bg-emerald-500/10 text-emerald-200 border border-emerald-500/20', rose: 'bg-rose-500/10 text-rose-200 border border-rose-500/20',
  //   }[tone];
  /** The "what can change" banner in the panel's tone: on white, 800 text on a 50 wash with a 200 rule. */
  bannerClass(tone: Tone): string {
    return this.light
      ? {
        amber: 'bg-amber-50 text-amber-800 border border-amber-200', sky: 'bg-sky-50 text-sky-800 border border-sky-200',
        emerald: 'bg-emerald-50 text-emerald-800 border border-emerald-200', rose: 'bg-rose-50 text-rose-800 border border-rose-200',
      }[tone]
      : {
        amber: 'bg-amber-500/10 text-amber-200 border border-amber-500/20', sky: 'bg-sky-500/10 text-sky-200 border border-sky-500/20',
        emerald: 'bg-emerald-500/10 text-emerald-200 border border-emerald-500/20', rose: 'bg-rose-500/10 text-rose-200 border border-rose-500/20',
      }[tone];
  }
  private fieldKey(path: readonly string[], name: string): string { return [...path, name].join('›'); }
}
